import React from "react";
import { motion } from "framer-motion";
import {
  Rocket, CheckCircle2, Hash, Flame, Trophy, Trees, Sparkles, Users,
  BookOpen, Target, Lightbulb, AlertTriangle,
} from "lucide-react";

const SECTIONS = [
  {
    id: "start",
    icon: Rocket,
    title: "Quick start",
    body: [
      "Tap New Habit (top-right of the Dashboard) or open the AI Coach → Plan Builder to generate habits from a goal.",
      "Give your habit a title, pick a category and colour, and choose a reminder time.",
      "Each day, mark your habits done. That's it — small steps, every day.",
    ],
  },
  {
    id: "types",
    icon: Hash,
    title: "Two habit types",
    body: [
      "Check-off: a simple done / not-done habit (e.g. Meditate). Tap Mark Done to complete it.",
      "Countable: track a number toward a target (e.g. Drink 8 glasses). Use + and − to log progress; the goal completes automatically when you hit the target.",
    ],
  },
  {
    id: "streaks",
    icon: Flame,
    title: "Streaks & 'never miss twice'",
    body: [
      "Your streak counts the consecutive days you've completed a habit.",
      "If you miss yesterday and haven't done it today, the habit shows a 'Don't miss twice' badge — a gentle nudge to get back on track before a slip becomes a habit.",
    ],
  },
  {
    id: "xp",
    icon: Trophy,
    title: "XP, levels & achievements",
    body: [
      "Every completion earns +10 XP (+5 bonus once a habit's streak hits 7 days).",
      "Every 100 XP levels you up — watch for the level-up celebration.",
      "Unlock 10 achievements for milestones like your first tick, a 30-day streak, or a perfect day. See them all on the Garden page.",
    ],
  },
  {
    id: "garden",
    icon: Trees,
    title: "Momentum Garden",
    body: [
      "Your garden grows a new plant with every habit you complete — a living picture of your consistency.",
      "The sun in the corner reflects your best current streak. Keep completing habits to fill the whole plot.",
    ],
  },
  {
    id: "coach",
    icon: Sparkles,
    title: "AI Coach & Plan Builder",
    body: [
      "Coach Chat: ask for motivation or advice — it knows your habits and streaks and replies with practical tips.",
      "Plan Builder: type a goal like 'sleep better' and get 3–5 small, specific habits. Tap Add all to drop them straight onto your dashboard.",
    ],
  },
  {
    id: "squad",
    icon: Users,
    title: "Your Squad",
    body: [
      "Add friends by email to build a shared XP leaderboard.",
      "Create or join group challenges — each one has its own leaderboard ranked by completions since it started. Great for a little friendly competition.",
    ],
  },
  {
    id: "checkin",
    icon: BookOpen,
    title: "Daily check-in & weekly review",
    body: [
      "Log how your day felt with a mood and a quick note.",
      "Open the Weekly Review to capture your win, what got in the way, and what you'll do next time — the loop that turns tracking into real improvement.",
    ],
  },
];

const PRINCIPLES = [
  { icon: Target, text: "Start ridiculously small — make habits so easy they're almost embarrassing to skip." },
  { icon: Flame, text: "Never miss twice. A slip is fine; two in a row is how habits break." },
  { icon: CheckCircle2, text: "Aim for 80%, not perfect. Consistency beats intensity." },
  { icon: Lightbulb, text: "Anchor new habits to something you already do every day." },
];

export default function HowTo() {
  return (
    <div className="space-y-8 pb-24 md:pb-8" data-testid="howto-page">
      <div>
        <p className="text-xs font-medium uppercase tracking-wider text-emerald-400 mb-2 flex items-center gap-2">
          <BookOpen className="w-3.5 h-3.5" /> Guide
        </p>
        <h1 className="font-display text-3xl sm:text-4xl font-black tracking-tight">How Momentum works</h1>
        <p className="text-white mt-2 max-w-2xl">Everything you need to build habits that actually stick — one tick at a time.</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        {SECTIONS.map((s, i) => (
          <motion.div
            key={s.id}
            data-testid={`howto-section-${s.id}`}
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: i * 0.05 }}
            className="p-5 sm:p-6 rounded-2xl border border-[#242417] bg-[#12141C] shadow-xl"
          >
            <div className="flex items-center gap-3 mb-4">
              <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-400 flex items-center justify-center glow-emerald shrink-0">
                <s.icon className="w-5 h-5 text-black" />
              </div>
              <h2 className="font-display text-lg font-semibold">{s.title}</h2>
            </div>
            <ul className="space-y-2.5">
              {s.body.map((line, j) => (
                <li key={j} className="flex gap-2.5 text-sm text-white">
                  <CheckCircle2 className="w-4 h-4 text-emerald-400 shrink-0 mt-0.5" />
                  <span>{line}</span>
                </li>
              ))}
            </ul>
          </motion.div>
        ))}
      </div>

      {/* Philosophy */}
      <div className="p-5 sm:p-8 rounded-2xl border border-emerald-500/30 bg-gradient-to-b from-[#14150E] to-[#12141C] shadow-xl">
        <div className="flex items-center gap-2 mb-5">
          <Lightbulb className="w-5 h-5 text-emerald-400" />
          <h2 className="font-display text-xl sm:text-2xl font-bold tracking-tight">The mindset</h2>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {PRINCIPLES.map((p, i) => (
            <div key={i} className="flex gap-3 p-4 rounded-xl bg-white/[0.03] border border-[#242417]">
              <p.icon className="w-5 h-5 text-emerald-400 shrink-0 mt-0.5" />
              <span className="text-sm text-white">{p.text}</span>
            </div>
          ))}
        </div>
        <div className="mt-6 flex items-start gap-3 p-4 rounded-xl bg-amber-500/10 border border-amber-500/30">
          <AlertTriangle className="w-5 h-5 text-amber-400 shrink-0 mt-0.5" />
          <span className="text-sm text-white">
            Missed a day? No judgement — just do it today. Keep missing the same habit? It's probably too big. Halve it or move it to an easier moment.
          </span>
        </div>
      </div>
    </div>
  );
}
